import React, { useEffect, useRef, useState } from 'react';
import { FiFilm, FiImage, FiLayers, FiLoader } from 'react-icons/fi';

import { useFrameImage } from './useFrameImage';
import type { QuickInferenceStatus } from '../../../helpers/api/quick-inference/quick-inference';

type FileKind = 'dicom' | 'video' | 'image';

interface Props {
  inferenceId?: string | null;
  status?: QuickInferenceStatus | string | null;
  kind?: FileKind;
  className?: string;
  iconSize?: number;
}

function KindIcon({ kind, size = 16 }: { kind: FileKind; size?: number }) {
  if (kind === 'video') return <FiFilm size={size} className="text-violet-400" />;
  if (kind === 'image') return <FiImage size={size} className="text-emerald-400" />;
  return <FiLayers size={size} className="text-blue-400" />;
}

export function QuickInferenceMediaThumb({
  inferenceId,
  status,
  kind = 'image',
  className = '',
  iconSize = 16,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const canLoad =
    Boolean(inferenceId) &&
    status !== 'pending' &&
    status !== 'fail' &&
    status != null;

  useEffect(() => {
    const el = ref.current;
    if (!el || !canLoad) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoad]);

  const { url, loading, failed } = useFrameImage(
    visible && canLoad ? inferenceId! : '',
    0
  );

  return (
    <div ref={ref} className={`relative overflow-hidden bg-gray-900 ${className}`}>
      {!canLoad ? (
        <div className="flex h-full w-full items-center justify-center">
          <KindIcon kind={kind} size={iconSize} />
        </div>
      ) : !visible || loading ? (
        <div className="flex h-full w-full items-center justify-center">
          <FiLoader size={Math.min(iconSize, 14)} className="animate-spin text-blue-400" />
        </div>
      ) : failed || !url ? (
        <div className="flex h-full w-full items-center justify-center">
          <KindIcon kind={kind} size={iconSize} />
        </div>
      ) : (
        <img src={url} alt="" className="h-full w-full object-cover" />
      )}
    </div>
  );
}
